import { StylePropertyDefinition } from '../../types/common';

/**
 * MarkdownEditor's chrome, as theme variables.
 *
 * `MarkdownEditorStyle.tsx` welds its palette into a template string that is
 * emitted BEFORE `processStyleDefinition`, so declaring a property here
 * overrides the literal by cascade alone. Every selector is written at the base
 * rule's own specificity so it wins on the tie, and `np: true` everywhere
 * because a comma-separated `sel` only gets the automatic prefix on its first
 * branch.
 *
 * Every default reproduces what the component renders today: where the base CSS
 * reads a theme variable through its `t()` helper the default is that same
 * `<variable>` reference, and where it hard-codes a literal the default is that
 * literal.
 *
 * Two things here are deliberately NOT exposed:
 * - `._primaryButton` sets its background, colour and border with `!important`,
 *   so a property here could never win. It needs the `!important` dropped from
 *   the base sheet first.
 * - `._addButton` is declared twice in the base sheet with the same selector,
 *   so the later block decides. The defaults below are that block's EFFECTIVE
 *   values (a black pill), not the earlier `<colorOne>` one.
 */

const DIALOG_INPUT_SEL = [
	'.comp.compMarkdownEditor ._linkInput',
	'.comp.compMarkdownEditor ._searchInput',
].join(', ');

const DIALOG_BUTTON_SEL = [
	'.comp.compMarkdownEditor ._linkDialogButtons button',
	'.comp.compMarkdownEditor ._cancelButton',
	'.comp.compMarkdownEditor ._addLinkButton',
].join(', ');

// Both pills are solid black with white glyphs today and read as one control.
const BLACK_PILL_SEL = [
	'.comp.compMarkdownEditor ._addButton',
	'.comp.compMarkdownEditor ._closeaddButton',
].join(', ');

export const styleProperties: Array<StylePropertyDefinition> = [
	// ─── Tab bar ───
	{
		gn: 'Tab Bar',
		dn: 'Background',
		n: 'markdownEditorTabBarBackground',
		dv: '<surfaceColorOne>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._tabBar',
		np: true,
	},
	{
		gn: 'Tab Bar',
		dn: 'Height',
		n: 'markdownEditorTabBarHeight',
		dv: '60px',
		cp: 'height',
		sel: '.comp.compMarkdownEditor ._tabBar',
		np: true,
	},
	{
		gn: 'Tab Bar',
		dn: 'Padding',
		n: 'markdownEditorTabBarPadding',
		dv: '10px 20px',
		cp: 'padding',
		sel: '.comp.compMarkdownEditor ._tabBar',
		np: true,
	},
	{
		gn: 'Tab Bar',
		dn: 'Separator Colour',
		n: 'markdownEditorTabSeparatorColor',
		dv: '<borderColorNine>',
		cp: 'border-right-color',
		sel: '.comp.compMarkdownEditor ._tabSeparator',
		np: true,
	},
	{
		gn: 'Tab Bar',
		dn: 'Write Tab Accent',
		de: 'The underline on the active Write tab.',
		n: 'markdownEditorWriteTabAccent',
		dv: '#016a70',
		cp: 'border-bottom-color',
		sel: '.comp.compMarkdownEditor ._tabBar ._tab._write._active',
		np: true,
	},
	{
		gn: 'Tab Bar',
		dn: 'Doc Tab Accent',
		n: 'markdownEditorDocTabAccent',
		dv: '#FF3e3e',
		cp: 'border-bottom-color',
		sel: '.comp.compMarkdownEditor ._tabBar ._tab._doc._active',
		np: true,
	},
	{
		gn: 'Tab Bar',
		dn: 'Preview Tab Accent',
		n: 'markdownEditorPreviewTabAccent',
		dv: '#3f4cc0',
		cp: 'border-bottom-color',
		sel: '.comp.compMarkdownEditor ._tabBar ._tab._preview._active',
		np: true,
	},

	// ─── Editing surface ───
	{
		gn: 'Editor',
		dn: 'Textarea Border Colour',
		n: 'markdownEditorTextAreaBorderColor',
		dv: '<borderColorNine>',
		cp: 'border-color',
		sel: '.comp.compMarkdownEditor textarea',
		np: true,
	},
	{
		gn: 'Editor',
		dn: 'Textarea Background',
		de: 'Unset by default, so the textarea keeps the browser default. Set it for a dark theme.',
		n: 'markdownEditorTextAreaBackground',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor textarea',
		np: true,
	},
	{
		gn: 'Editor',
		dn: 'Textarea Text Colour',
		de: 'Unset by default. A form control does not inherit colour, so a dark theme needs this.',
		n: 'markdownEditorTextAreaColor',
		cp: 'color',
		sel: '.comp.compMarkdownEditor textarea',
		np: true,
	},
	{
		gn: 'Editor',
		dn: 'Textarea Font Family',
		de: 'Unset by default.',
		n: 'markdownEditorTextAreaFontFamily',
		cp: 'font-family',
		sel: '.comp.compMarkdownEditor textarea',
		np: true,
	},

	// ─── Toolbar ───
	{
		gn: 'Toolbar',
		dn: 'Panel Background',
		n: 'markdownEditorToolbarBackground',
		dv: '<colorSeven>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._filterPanel',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Panel Border Colour',
		n: 'markdownEditorToolbarBorderColor',
		dv: 'rgba(0, 0, 0, 0.10)',
		cp: 'border-color',
		sel: '.comp.compMarkdownEditor ._filterPanel',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Panel Border Radius',
		n: 'markdownEditorToolbarBorderRadius',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compMarkdownEditor ._filterPanel',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Panel Height',
		n: 'markdownEditorToolbarHeight',
		dv: '40px',
		cp: 'height',
		sel: '.comp.compMarkdownEditor ._filterPanel',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Button Border Radius',
		n: 'markdownEditorFormatButtonBorderRadius',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compMarkdownEditor ._formatbutton',
		np: true,
	},
	// The active and hover rules tie on specificity, so the base sheet resolves
	// them by source order and these two entries have to keep that order.
	{
		gn: 'Toolbar',
		dn: 'Button Active Background',
		n: 'markdownEditorFormatButtonActiveBackground',
		dv: '<surfaceColorTwo>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._formatbutton._active',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Button Active Border Colour',
		n: 'markdownEditorFormatButtonActiveBorderColor',
		dv: '<borderColorNine>',
		cp: 'border-color',
		sel: '.comp.compMarkdownEditor ._formatbutton._active',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Button Hover Background',
		n: 'markdownEditorFormatButtonHoverBackground',
		dv: '<surfaceColorTwo>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._formatbutton:hover',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Icon Colour',
		de: 'The toolbar and dropdown glyphs draw in currentColor, so this one colour follows the theme (light ink on a dark theme).',
		n: 'markdownEditorToolbarIconColor',
		dv: '<fontColorOne>',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._filterPanel svg',
		np: true,
	},
	{
		gn: 'Toolbar',
		dn: 'Button Separator Colour',
		n: 'markdownEditorButtonSeparatorColor',
		dv: '#e1e4e8',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._buttonSeperator',
		np: true,
	},

	// ─── Dropdown menus ───
	{
		gn: 'Dropdown',
		dn: 'Background',
		n: 'markdownEditorDropdownBackground',
		dv: '<colorSeven>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._dropdown',
		np: true,
	},
	{
		gn: 'Dropdown',
		dn: 'Item Text Colour',
		n: 'markdownEditorDropdownItemColor',
		dv: '<fontColorTwo>',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._dropdownItem',
		np: true,
	},
	{
		gn: 'Dropdown',
		dn: 'Item Font Size',
		n: 'markdownEditorDropdownItemFontSize',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compMarkdownEditor ._dropdownItem',
		np: true,
	},
	{
		gn: 'Dropdown',
		dn: 'Item Border Radius',
		n: 'markdownEditorDropdownItemBorderRadius',
		dv: '40px',
		cp: 'border-radius',
		sel: '.comp.compMarkdownEditor ._dropdownItem',
		np: true,
	},
	{
		gn: 'Dropdown',
		dn: 'Item Hover Background',
		n: 'markdownEditorDropdownItemHoverBackground',
		dv: '<surfaceColorTwo>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._dropdownItem:hover',
		np: true,
	},
	{
		gn: 'Dropdown',
		dn: 'Item Hover Text Colour',
		n: 'markdownEditorDropdownItemHoverColor',
		dv: '<fontColorOne>',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._dropdownItem:hover',
		np: true,
	},

	// ─── Link dialog ───
	{
		gn: 'Dialog',
		dn: 'Scrim',
		de: 'The wash over the page behind a dialog.',
		n: 'markdownEditorScrimBackground',
		dv: 'rgba(0, 0, 0, 0.5)',
		cp: 'background',
		sel: '.comp.compMarkdownEditor ._popupBackground',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Background',
		n: 'markdownEditorDialogBackground',
		dv: '<colorSeven>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._linkDialog',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Border Radius',
		n: 'markdownEditorDialogBorderRadius',
		dv: '8px',
		cp: 'border-radius',
		sel: '.comp.compMarkdownEditor ._linkDialog',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Title Colour',
		n: 'markdownEditorDialogTitleColor',
		dv: '<fontColorOne>',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._linkDialog h3',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Title Font Size',
		n: 'markdownEditorDialogTitleFontSize',
		dv: '18px',
		cp: 'font-size',
		sel: '.comp.compMarkdownEditor ._linkDialog h3',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Label Colour',
		n: 'markdownEditorDialogLabelColor',
		dv: '<fontColorTwo>',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._inputGroup label',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Input Border Colour',
		n: 'markdownEditorDialogInputBorderColor',
		dv: '<borderColorTen>',
		cp: 'border-color',
		sel: DIALOG_INPUT_SEL,
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Input Border Radius',
		n: 'markdownEditorDialogInputBorderRadius',
		dv: '4px',
		cp: 'border-radius',
		sel: DIALOG_INPUT_SEL,
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Input Focus Border Colour',
		n: 'markdownEditorDialogInputFocusBorderColor',
		dv: '<colorOne>',
		cp: 'border-color',
		sel: '.comp.compMarkdownEditor ._linkInput:focus',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Button Border Colour',
		n: 'markdownEditorDialogButtonBorderColor',
		dv: '<borderColorTen>',
		cp: 'border-color',
		sel: DIALOG_BUTTON_SEL,
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Button Border Radius',
		n: 'markdownEditorDialogButtonBorderRadius',
		dv: '4px',
		cp: 'border-radius',
		sel: DIALOG_BUTTON_SEL,
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Neutral Button Background',
		n: 'markdownEditorDialogButtonBackground',
		dv: '<surfaceColorTwo>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._linkDialogButtons button',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Cancel Button Background',
		n: 'markdownEditorCancelButtonBackground',
		dv: '#ff0000',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._cancelButton',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Add Link Button Background',
		n: 'markdownEditorAddLinkButtonBackground',
		dv: '<colorOne>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._addLinkButton',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Close Button Hover Background',
		n: 'markdownEditorCloseButtonHoverBackground',
		dv: '#F32013',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._closeButton:hover',
		np: true,
	},
	{
		gn: 'Dialog',
		dn: 'Close Button Hover Colour',
		n: 'markdownEditorCloseButtonHoverColor',
		dv: '#ffffff',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._closeButton:hover',
		np: true,
	},

	// ─── Insert-component picker ───
	{
		gn: 'Component Picker',
		dn: 'Trigger Background',
		de: 'The round add / close pills that open and close the picker.',
		n: 'markdownEditorAddButtonBackground',
		dv: '#000000',
		cp: 'background',
		sel: BLACK_PILL_SEL,
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Trigger Colour',
		n: 'markdownEditorAddButtonColor',
		dv: '#ffffff',
		cp: 'color',
		sel: BLACK_PILL_SEL,
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Trigger Hover Background',
		n: 'markdownEditorAddButtonHoverBackground',
		dv: '<surfaceColorThree>',
		cp: 'background',
		sel: '.comp.compMarkdownEditor ._addButton:hover',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Trigger Hover Colour',
		n: 'markdownEditorAddButtonHoverColor',
		dv: '<fontColorOne>',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._addButton:hover',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Popup Background',
		n: 'markdownEditorPickerBackground',
		dv: '<colorSeven>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._componentPopup',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Popup Border Colour',
		n: 'markdownEditorPickerBorderColor',
		dv: '#d0d7de',
		cp: 'border-color',
		sel: '.comp.compMarkdownEditor ._componentPopup',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Popup Border Radius',
		n: 'markdownEditorPickerBorderRadius',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compMarkdownEditor ._componentPopup',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Tile Background',
		n: 'markdownEditorComponentButtonBackground',
		dv: '#ffffff',
		cp: 'background',
		sel: '.comp.compMarkdownEditor ._componentButton',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Tile Border Colour',
		n: 'markdownEditorComponentButtonBorderColor',
		dv: '<borderColorNine>',
		cp: 'border-color',
		sel: '.comp.compMarkdownEditor ._componentButton',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Tile Hover Background',
		n: 'markdownEditorComponentButtonHoverBackground',
		dv: '<surfaceColorTwo>',
		cp: 'background-color',
		sel: '.comp.compMarkdownEditor ._componentButton:hover',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Footer Background',
		n: 'markdownEditorFooterBackground',
		dv: '#000000',
		cp: 'background',
		sel: '.comp.compMarkdownEditor ._footer',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Footer Divider Colour',
		n: 'markdownEditorFooterBorderColor',
		dv: '<borderColorNine>',
		cp: 'border-top-color',
		sel: '.comp.compMarkdownEditor ._footer',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Browse All Background',
		n: 'markdownEditorBrowseAllBackground',
		dv: '#000000',
		cp: 'background',
		sel: '.comp.compMarkdownEditor ._browseAll',
		np: true,
	},
	{
		gn: 'Component Picker',
		dn: 'Browse All Colour',
		n: 'markdownEditorBrowseAllColor',
		dv: '#ffffff',
		cp: 'color',
		sel: '.comp.compMarkdownEditor ._browseAll',
		np: true,
	},
];

export const styleDefaults = new Map<string, string>(
	styleProperties.filter(e => !!e.dv).map(({ n, dv }) => [n, dv!]),
);

export const stylePropertiesForTheme: Array<StylePropertyDefinition> = styleProperties;
